import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';
import { dateToApiFormat } from '../../helpers/dateHelper';
import {
  ReadEmployee,
  useGetEmployeeSkillsQuery,
  useGetEmployeeWorkRulesQuery,
  useLocationWorkingPatternsQuery
} from '../gql/graphql.generated';
import { useLocationService } from '../location/LocationService';
import { useEmployeeList } from '../staff/useEmployeeList';
import { defaultStaffSettings, StaffSettings, StaffWithSettings, WorkingPatterns } from './systemSettings.types';
import { useDefaultWorkingPattern } from './useDefaultWorkingPattern';

interface StaffSettingsStore {
  settings: StaffWithSettings[];
  setSettings: (newSettings: StaffWithSettings[]) => void;
  settingsForStaffMember: (employeeId: string) => StaffSettings;
  refetchAllSettings: () => void;
}

export const StaffSettingsContext = createContext<StoreApi<StaffSettingsStore> | null>(null);

export const StaffSettingsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { allEmployees: employees, refetch: refetchEmployees } = useEmployeeList({ includeDeleted: true });

  const employeeIds = useMemo(() => employees.map(emp => emp.identityId), [employees]);
  const [{ data: workRulesData }, refetchWorkRules] = useGetEmployeeWorkRulesQuery({
    variables: { employeeIds: employeeIds }
  });

  const workRules = workRulesData?.workRulesByEmployeeIds || [];

  const selectedLocationId = useLocationService(state => state.selectedLocation?.id);
  const [{ data: locationWorkingPatternsData }, refetchWorkingPatterns] = useLocationWorkingPatternsQuery({
    variables: {
      locationId: selectedLocationId || 0,
      date: dateToApiFormat(new Date())
    },
    pause: !selectedLocationId
  });

  const workingPatterns = locationWorkingPatternsData?.locationWorkingPatterns;

  const [{ data: skillData }, refetchSkills] = useGetEmployeeSkillsQuery({ variables: { employeeIds } });
  const skillList = skillData?.employeeSkillsByEmployeeIds || [];

  const defaultPattern = useDefaultWorkingPattern();

  const store = useMemo(() => {
    //@ts-ignore
    const settings: StaffWithSettings[] = employees.map(employee => {
      const rules = workRules.find(wr => wr.identityId === employee.identityId);

      const employeePattern = workingPatterns?.employeePatterns?.find(
        pattern => pattern.identityId === employee.identityId
      );

      let workingPatternsObject: WorkingPatterns[] = [];

      if (employeePattern) {
        const workingPatternsFromData = employeePattern.periods![0]?.patterns;

        if (workingPatternsFromData?.length) {
          workingPatternsFromData?.forEach(workingPattern => {
            workingPatternsObject.push({
              day: workingPattern.dayNumber!,
              slotNumber: workingPattern.slotNumber || 0,
              startTime: workingPattern.startTime || 0,
              endTime: workingPattern.endTime || 0,
              id: workingPattern.id
            });
          });
        }
      }

      if (workingPatternsObject.length === 0) {
        workingPatternsObject = defaultPattern;
      }

      const assignedSkills: string[] = [];
      const employeeSkills = skillList.find(s => s.employeeId === employee.identityId);
      employeeSkills?.skills?.forEach(skill => assignedSkills.push(skill.skillId));

      const assignedPersonaIds: string[] = employee.personas?.map(p => p.id) || [];
      const assignedLocationIds: number[] = employee.locations?.map(p => p.id) || [];

      const properties = employee.properties ? JSON.parse(employee.properties) : {};

      const employeeSettings: StaffSettings = {
        ...defaultStaffSettings,
        phoneNumber: employee.phoneNumber ?? defaultStaffSettings.phoneNumber,
        email: employee.email ?? defaultStaffSettings.email,
        assignedSkills,
        dateOfBirth: employee.dateOfBirth && new Date(employee.dateOfBirth),
        hiredDate: employee.hireDate && new Date(employee.hireDate),
        endProbationDate: employee.endOfProbationDate && new Date(employee.endOfProbationDate),
        leftCompanyDate: employee.leaveDate && new Date(employee.leaveDate),
        hasBeenDeleted: !employee.isRosterable,
        assignedPersonaIds,
        assignedLocationIds,
        notes: properties.notes
      };

      if (rules) {
        employeeSettings.minMonthlyHours =
          rules.minHoursPerMonth === undefined ? defaultStaffSettings.minMonthlyHours : rules.minHoursPerMonth;
        employeeSettings.maxMonthlyHours =
          rules.maxHoursPerMonth === undefined ? defaultStaffSettings.maxMonthlyHours : rules.maxHoursPerMonth;

        employeeSettings.minWeeklyHours =
          rules.minHoursPerWeek === undefined ? defaultStaffSettings.minWeeklyHours : rules.minHoursPerWeek;
        employeeSettings.maxWeeklyHours =
          rules.maxHoursPerWeek === undefined ? defaultStaffSettings.maxWeeklyHours : rules.maxHoursPerWeek;
      }

      if (workingPatternsObject.length > 0) {
        employeeSettings.workingPatterns = workingPatternsObject;
      }

      return {
        employee: employee as ReadEmployee,
        settings: employeeSettings
      };
    });

    return createStore<StaffSettingsStore>()((set, get) => ({
      settings,
      setSettings: settings => set({ settings }),
      settingsForStaffMember: employeeId => {
        const staffSettings = get().settings.find(emp => emp.employee.identityId === employeeId);

        if (!staffSettings) {
          return {} as StaffSettings;
        }

        return staffSettings.settings;
      },
      refetchAllSettings: () => {
        refetchEmployees({ requestPolicy: 'network-only' });
        refetchWorkingPatterns({ requestPolicy: 'network-only' });
        refetchWorkRules({ requestPolicy: 'network-only' });
        refetchSkills({ requestPolicy: 'network-only' });
      }
    }));
  }, [
    employees,
    workRules,
    locationWorkingPatternsData?.locationWorkingPatterns?.employeePatterns,
    skillList,
    defaultPattern,
    refetchEmployees,
    refetchWorkingPatterns,
    refetchWorkRules,
    refetchSkills
  ]);

  return <StaffSettingsContext.Provider value={store}>{children}</StaffSettingsContext.Provider>;
};

type SelectorReturn<S extends (s: StaffSettingsStore) => any> = ReturnType<S>;
export function useStaffSettings<S extends (s: StaffSettingsStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(StaffSettingsContext);
  if (!context) {
    throw new Error('useStaffSettings must be used within a StaffSettingsProvider');
  }
  return useStore(context, selector);
}
